import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { useOmniAuth } from '@omni/auth'
import { Loader2, X } from 'lucide-react'
import { loadPeekCard, type PeekCard } from '../lib/context-cards'
import { makeMailApiGet } from '../lib/mail-api'
import { shellNavigate } from '../lib/navigation'
import { useShellStore } from '../lib/store'

/**
 * The context-card drawer (Correction 3): peek-* events and
 * shell.openContextCard land here; the card is resolved by whichever
 * surface's provider claims the ref kind.
 */
export function PeekDrawer() {
  const ref = useShellStore((s) => s.peekRef)
  const close = useShellStore((s) => s.closeContextCard)
  const { getClient } = useOmniAuth()
  const mailApiGet = useMemo(() => makeMailApiGet(getClient('mail')), [getClient])

  const [card, setCard] = useState<PeekCard | null>(null)
  const [loading, setLoading] = useState(false)

  useEffect(() => {
    if (!ref) {
      setCard(null)
      return
    }
    let cancelled = false
    setLoading(true)
    setCard(null)
    loadPeekCard(ref, mailApiGet, getClient('crm'))
      .then((c) => {
        if (!cancelled) setCard(c)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })
    return () => {
      cancelled = true
    }
  }, [ref, mailApiGet])

  useEffect(() => {
    if (!ref) return
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') close()
    }
    window.addEventListener('keydown', onKeyDown)
    return () => window.removeEventListener('keydown', onKeyDown)
  }, [ref, close])

  if (!ref) return null

  return (
    <div className="fixed inset-0 z-40" onMouseDown={close}>
      <div
        className="absolute inset-y-0 right-0 flex w-96 max-w-full flex-col border-l border-border bg-surface-1 shadow-2xl"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-border px-4 py-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-text-muted">
            {ref.kind}
          </p>
          <button
            type="button"
            title="Close"
            onClick={close}
            className="flex h-7 w-7 items-center justify-center rounded-md text-text-muted hover:bg-surface-2 hover:text-text-primary"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-4">
          {loading && (
            <div className="flex items-center gap-2 text-sm text-text-muted">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          )}
          {!loading && card && <CardBody card={card} />}
        </div>
      </div>
    </div>
  )
}

function CardBody({ card }: { card: PeekCard }) {
  const close = useShellStore((s) => s.closeContextCard)

  if (card.type === 'empty') {
    return <p className="text-sm text-text-muted">{card.reason}</p>
  }
  if (card.type === 'node') {
    return <>{card.node}</>
  }
  return (
    <div>
      <h2 className="text-base font-semibold text-text-primary">
        {card.card.title}
      </h2>
      {card.card.subtitle && (
        <p className="mt-0.5 text-sm text-text-secondary">{card.card.subtitle}</p>
      )}
      <div className="mt-4 flex flex-col gap-4">
        {card.card.sections.map((section) => (
          <section key={section.heading}>
            <h3 className="mb-1.5 text-xs font-semibold uppercase tracking-wider text-text-muted">
              {section.heading}
            </h3>
            <dl className="flex flex-col gap-1">
              {section.rows.map((row, i) => (
                <div key={`${row.label}-${i}`} className="flex gap-2 text-sm">
                  <dt className="w-28 shrink-0 text-text-muted">{row.label}</dt>
                  <dd className="min-w-0 flex-1 text-text-primary">
                    {row.href ? (
                      <button
                        type="button"
                        className="truncate text-left text-accent hover:underline"
                        onClick={() => {
                          shellNavigate(row.href!)
                          close()
                        }}
                      >
                        {row.value as ReactNode}
                      </button>
                    ) : (
                      (row.value as ReactNode)
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          </section>
        ))}
      </div>
    </div>
  )
}
